import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV } from "../data/site";
import { Logo } from "./Logo";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-light border-b border-slate-200/70 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="container-x flex items-center justify-between">
        <Logo dark={!scrolled} />

        <nav className="hidden items-center gap-1 lg:flex" data-testid="desktop-nav">
          {NAV.map((item) => {
            const active = pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`relative rounded-full px-4 py-2 text-sm font-medium transition-colors duration-300 ${
                  active
                    ? (scrolled ? "text-[#07111F]" : "text-white")
                    : (scrolled ? "text-slate-500 hover:text-[#07111F]" : "text-slate-300 hover:text-white")
                }`}
              >
                {item.label}
                {active && (
                  <motion.span layoutId="nav-dot" className="absolute inset-x-4 -bottom-0.5 h-0.5 accent-bg rounded-full" />
                )}
              </Link>
            );
          })}
        </nav>

        <div className="hidden lg:block">
          <Link to="/contact" className="btn-primary" data-testid="header-cta">
            Let's Talk <ArrowUpRight size={16} />
          </Link>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className={`grid h-10 w-10 place-items-center rounded-xl border lg:hidden transition-colors duration-300 ${
            scrolled ? "border-slate-200 text-[#07111F]" : "border-white/20 text-white"
          }`}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden lg:hidden"
            data-testid="mobile-nav"
          >
            <div className="container-x mt-3 glass-light rounded-3xl border border-slate-200 p-4">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <Link
                    to={item.to}
                    className="block rounded-2xl px-4 py-3 text-base font-medium text-[#07111F] hover:bg-slate-100"
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  >
                    {item.label}
                  </Link>
                </motion.div>
              ))}
              <Link to="/contact" className="btn-primary mt-2 w-full" data-testid="mobile-header-cta">
                Let's Talk <ArrowUpRight size={16} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
